import { Injectable } from '@angular/core';
import { NetworkLogEntry } from '../types/network-log-entry';
import { RedactionService } from '../../security/redaction.service';
import { SessionTimelineCollector } from './session-timeline.collector';
import { RingBuffer } from '../storage/ring-buffer';

type FetchType = typeof fetch;

@Injectable({
  providedIn: 'root',
})
export class NetworkCaptureCollector {
  private readonly entries = new RingBuffer<NetworkLogEntry>(100);
  private started = false;
  private originalFetch?: FetchType;
  private originalOpen?: XMLHttpRequest['open'];
  private originalSend?: XMLHttpRequest['send'];
  private originalSetRequestHeader?: XMLHttpRequest['setRequestHeader'];

  constructor(
    private readonly sessionTimeline: SessionTimelineCollector,
    private readonly redactionService: RedactionService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.patchFetch();
    this.patchXhr();
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;

    if (this.originalFetch) {
      window.fetch = this.originalFetch;
    }

    if (this.originalOpen) {
      XMLHttpRequest.prototype.open = this.originalOpen;
    }

    if (this.originalSend) {
      XMLHttpRequest.prototype.send = this.originalSend;
    }

    if (this.originalSetRequestHeader) {
      XMLHttpRequest.prototype.setRequestHeader = this.originalSetRequestHeader;
    }

    this.originalFetch = undefined;
    this.originalOpen = undefined;
    this.originalSend = undefined;
    this.originalSetRequestHeader = undefined;
  }

  clear(): void {
    this.entries.clear();
  }

  getEntries(): readonly NetworkLogEntry[] {
    return this.entries.toArray();
  }

  private patchFetch(): void {
    this.originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<FetchType>): Promise<Response> => {
      const start = performance.now();
      const request = args[0];
      const options = args[1] ?? {};
      let method = 'GET';
      let url = '';

      try {
        method = this.extractMethod(request, options);
        url = this.extractUrl(request);
      } catch {
        // ignore extraction errors — metadata is best-effort
      }

      let response: Response;
      try {
        response = await this.originalFetch!.apply(window, args);
      } catch (error) {
        // Network-level failure — capture and re-throw so the app still gets the error.
        try {
          const duration = Math.round(performance.now() - start);
          this.entries.push({
            kind: 'fetch',
            method,
            url: this.redactionService.redactUrl(url),
            status: 0,
            duration,
            timestamp: new Date().toISOString(),
            success: false,
            error: this.redactionService.redactText((error as Error)?.message ?? 'Fetch failed'),
          });
          this.sessionTimeline.recordRequestFailure(method, url, 0, duration, (error as Error)?.message);
        } catch {
          // diagnostic capture must never interfere with the error being re-thrown
        }
        throw error;
      }

      try {
        const duration = Math.round(performance.now() - start);
        this.entries.push({
          kind: 'fetch',
          method,
          url: this.redactionService.redactUrl(url),
          status: response.status,
          duration,
          timestamp: new Date().toISOString(),
          success: response.ok,
        });
        if (!response.ok) {
          this.sessionTimeline.recordRequestFailure(method, url, response.status, duration, `HTTP ${response.status}`);
        }
      } catch {
        // diagnostic capture must not affect the returned response
      }

      return response;
    };
  }

  private patchXhr(): void {
    this.originalOpen = XMLHttpRequest.prototype.open;
    this.originalSend = XMLHttpRequest.prototype.send;
    this.originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    const self = this;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string,
      async = true,
      username?: string | null,
      password?: string | null,
    ): void {
      (this as unknown as { __feedbackMeta?: { method: string; url: string; start: number } }).__feedbackMeta = {
        method,
        url,
        start: performance.now(),
      };
      self.originalOpen!.call(this, method, url, Boolean(async), username, password);
    };

      XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null): void {
        this.addEventListener('loadend', () => {
          try {
            const meta = (this as unknown as { __feedbackMeta?: { method: string; url: string; start: number } })
              .__feedbackMeta;

            if (!meta) {
              return;
            }

            const duration = Math.round(performance.now() - meta.start);
            self.entries.push({
              kind: 'xhr',
              method: meta.method,
              url: self.redactionService.redactUrl(meta.url),
              status: this.status || 0,
              duration,
              timestamp: new Date().toISOString(),
              success: this.status >= 200 && this.status < 400,
            });

            if (this.status < 200 || this.status >= 400) {
              self.sessionTimeline.recordRequestFailure(
                meta.method,
                meta.url,
                this.status || 0,
                duration,
                `HTTP ${this.status || 0}`,
              );
            }
          } catch {
            // diagnostic capture must be non-disruptive
          }
        });

        self.originalSend!.call(this, body as never);
      };

    XMLHttpRequest.prototype.setRequestHeader = function (header: string, value: string): void {
      self.originalSetRequestHeader!.call(this, header, value);
    };
  }

  private extractUrl(request: RequestInfo | URL): string {
    if (typeof request === 'string') {
      return request;
    }

    if (request instanceof URL) {
      return request.toString();
    }

    return request.url;
  }

  private extractMethod(request: RequestInfo | URL, options: RequestInit): string {
    if (options.method) {
      return options.method.toUpperCase();
    }

    if (typeof request !== 'string' && !(request instanceof URL)) {
      return request.method?.toUpperCase() || 'GET';
    }

    return 'GET';
  }
}
